<template>
  <div>
    <section-title
      content="2. Información de la Discapacidad y Médica"
      icon="fa fa-file-contract"
      reverse
    />
    <div class="disability-medical-info">
      <section class="section-panel category-panel">
        <div class="section-panel__title">Categoría de Discapacidad:</div>
        <div class="section-panel__body">
          <div class="category-field">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.disabilityCategory"
                :custom-error-msg="
                  'Favor de seleccionar la categoría de discapacidad.'
                "
                :disabled="readOnly"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Categoría de Discapacidad:"
                :options="disabilityCategories"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>

          <div
            class="diagnosis-selector"
            :class="{
              'diagnosis-selector--single-field': !isOtherDiagnosisSelected,
            }"
          >
            <div class="diagnosis-selector__field">
              <custom-drop-down
                v-model="model.diagnosis"
                :clearable="true"
                :disabled="readOnly"
                fieldkey="id"
                fieldname="name"
                label="Seleccione el Diagnóstico del Estudiante:"
                :options="availableDiagnosisOptions"
                placeholder="Seleccione"
                @input="onDiagnosisChange"
              />
            </div>

            <div
              v-if="isOtherDiagnosisSelected"
              class="diagnosis-selector__field"
            >
              <custom-input
                v-model="model.diagnosisSpecification"
                :disabled="readOnly"
                label="Especifique:"
                placeholder="Especifique"
              />
            </div>

            <action-button
              v-if="!readOnly"
              class="diagnosis-selector__button"
              i="fas fa-file-plus"
              variant="success"
              @click="addDiagnosis"
            >
              Añadir
            </action-button>
          </div>

          <error
            v-if="diagnosisError"
            class="mt-2"
            :message="diagnosisError"
            variant="no-border"
          />

          <div class="diagnosis-list">
            <div class="diagnosis-list__separator">
              <span>Diagnósticos añadidos</span>
            </div>

            <validation-provider
              v-slot="{ errors }"
              :rules="hasDiagnoses ? '' : 'required'"
            >
              <input
                :value="hasDiagnoses ? 'valid' : ''"
                class="d-none"
                type="text"
              />
              <error
                v-if="errors[0]"
                class="mb-2"
                message="Debe añadir al menos un diagnóstico antes de continuar."
                variant="no-border"
              />
            </validation-provider>

            <draggable
              v-model="model.diagnoses"
              handle=".diagnosis-row__drag"
              :disabled="readOnly"
              @end="onDiagnosisReordered"
            >
              <transition-group name="diagnosis-row" tag="div">
                <div
                  v-for="(diagnosis, index) in model.diagnoses"
                  :key="diagnosis.key || diagnosis.id || diagnosis.name"
                  class="diagnosis-row"
                >
                  <div class="diagnosis-row__leading">
                    <span class="diagnosis-row__index">{{ index + 1 }}</span>
                    <button
                      class="diagnosis-row__drag"
                      :disabled="readOnly"
                      type="button"
                    >
                      <i class="fas fa-grip-vertical" />
                    </button>
                    <span class="diagnosis-row__name">
                      {{ diagnosis.name }}
                    </span>
                  </div>

                  <div class="diagnosis-row__actions">
                    <span
                      class="diagnosis-row__badge"
                      :class="{
                        'diagnosis-row__badge--primary': index === 0,
                        'diagnosis-row__badge--secondary': index > 0,
                      }"
                    >
                      {{ index === 0 ? 'Primario' : 'Secundario' }}
                    </span>
                    <button
                      v-if="!readOnly"
                      class="diagnosis-row__remove"
                      type="button"
                      @click="removeDiagnosis(index)"
                    >
                      <i class="far fa-times-circle" />
                    </button>
                  </div>
                </div>
              </transition-group>
            </draggable>
          </div>
        </div>
      </section>

      <section class="section-panel">
        <div class="section-panel__title">Información Médica Suplementaria</div>
        <div class="section-panel__body">
          <div class="boolean-question">
            <p class="boolean-question__label">
              ¿Está el estudiante recibiendo algún tipo de tratamiento?
            </p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.isReceivingTreatment"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="receiving-treatment"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>

          <validation-provider
            v-slot="{ errors }"
            v-if="model.isReceivingTreatment"
            rules="required|max:5000"
          >
            <custom-text-area
              v-model="model.treatmentDescription"
              class="question-textarea"
              :custom-error-msg="errors[0]"
              :disabled="readOnly || !model.isReceivingTreatment"
              :error="errors[0]"
              label="Especifique el tipo de tratamiento:"
              :maxlength="5000"
              placeholder="Escribe comentario"
              show-char-count
              :count-classes="['d-block text-right']"
            />
          </validation-provider>

          <div class="boolean-question question-spacing">
            <p class="boolean-question__label">
              ¿Utiliza el estudiante algún tipo de medicamento?
            </p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.usesMedication"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="uses-medication"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>

          <validation-provider
            v-slot="{ errors }"
            v-if="model.usesMedication"
            rules="required|max:5000"
          >
            <custom-text-area
              v-model="model.medicationDescription"
              class="question-textarea"
              :custom-error-msg="errors[0]"
              :disabled="readOnly || !model.usesMedication"
              :error="errors[0]"
              label="Especifique cuales y dosis:"
              :maxlength="5000"
              placeholder="Escribe comentario"
              show-char-count
              :count-classes="['d-block text-right']"
            />
          </validation-provider>
        </div>
      </section>

      <section class="section-panel ambulatory-panel">
        <div class="section-panel__title">Ambulación</div>
        <div class="section-panel__body">
          <div
            class="boolean-question ambulatory-question ambulatory-question--divider"
          >
            <p class="boolean-question__label">
              1. ¿Camina independientemente?
            </p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.walksIndependently"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="walks-independently"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>

          <div class="short-field">
            <custom-input
              v-model="model.assistiveEquipmentDescription"
              :disabled="readOnly"
              label="2. ¿Utiliza equipo asistivo? (silla de ruedas, muletas, bastón, audífonos)"
            />
          </div>

          <div
            class="boolean-question ambulatory-question ambulatory-question--short"
          >
            <p class="boolean-question__label">3. ¿Puede viajar solo?</p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.canTravelAlone"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="can-travel-alone"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>

          <div
            class="boolean-question ambulatory-question ambulatory-question--last"
          >
            <p class="boolean-question__label">
              4. ¿Realiza actividades diarias del cuidado propio?
            </p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.performsDailySelfCareActivities"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="daily-self-care"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import CustomRadio from '@/components/common/CustomRadio';
import CustomTextArea from 'CustomTextArea';
import Draggable from 'vuedraggable';
import Error from '@/components/common/Error';
import SectionTitle from 'SectionTitle';
import studentService from '@/services/student';
import { getEligibilityDeterminationConditions } from '@/services/vocational-rehabilitation-referral/api';

export default {
  name: 'DisabilityMedicalInfoSection',
  components: {
    ActionButton,
    CustomDropDown,
    CustomInput,
    CustomRadio,
    CustomTextArea,
    Draggable,
    Error,
    SectionTitle,
  },
  props: {
    readOnly: Boolean,
    value: {
      type: Object,
      default: () => null,
    },
    disabilityMedicalInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      disabilityCategories: [],
      diagnosisOptions: [],
      diagnosisError: null,
      yesNoOptions: [
        {
          id: 1,
          label: 'Sí',
          value: true,
          check: false,
        },
        {
          id: 2,
          label: 'No',
          value: false,
          check: false,
        },
      ],
    };
  },
  computed: {
    model: {
      get() {
        return this.value || this.disabilityMedicalInfo || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    hasDiagnoses() {
      return (this.model.diagnoses || []).length > 0;
    },
    isOtherDiagnosisSelected() {
      return this.model.diagnosis?.id === 256;
    },
    availableDiagnosisOptions() {
      const selectedIds = (this.model.diagnoses || [])
        .filter((x) => (x.diagnosisId || x.id) !== 256)
        .map((x) => x.diagnosisId || x.id);

      return (this.diagnosisOptions || []).filter(
        (x) => !selectedIds.includes(x.id)
      );
    },
  },
  mounted() {
    this.ensureModelDefaults();
    this.loadDisabilityCategories();
    this.loadDiagnosisOptions();
  },
  watch: {
    'model.isReceivingTreatment'(value) {
      if (!value) {
        this.model.treatmentDescription = null;
      }
    },
    'model.usesMedication'(value) {
      if (!value) {
        this.model.medicationDescription = null;
      }
    },
  },
  methods: {
    ensureModelDefaults() {
      if (!Array.isArray(this.model.diagnoses)) {
        this.$set(this.model, 'diagnoses', []);
      }

      if (this.model.diagnosis === undefined) {
        this.$set(this.model, 'diagnosis', null);
      }

      if (this.model.diagnosisSpecification === undefined) {
        this.$set(this.model, 'diagnosisSpecification', null);
      }
    },
    async loadDisabilityCategories() {
      const { data } = await studentService.getImpediments();
      this.disabilityCategories = data || [];
    },
    async loadDiagnosisOptions() {
      const { data } = await getEligibilityDeterminationConditions(true);
      const options = data || [];
      const hasOther = options.some((x) => x.id === 256);

      this.diagnosisOptions = hasOther
        ? options
        : [...options, { id: 256, name: 'Otros' }];
    },
    onDiagnosisChange() {
      this.diagnosisError = null;

      if (!this.isOtherDiagnosisSelected) {
        this.$set(this.model, 'diagnosisSpecification', null);
      }
    },
    addDiagnosis() {
      if (this.readOnly) return;

      if (!this.model.diagnosis) {
        this.diagnosisError =
          'Debe seleccionar un diagnóstico antes de añadir.';
        return;
      }

      const list = [...(this.model.diagnoses || [])];
      const selected = this.model.diagnosis;
      const specification = (this.model.diagnosisSpecification || '').trim();

      if (this.isOtherDiagnosisSelected && !specification) {
        this.diagnosisError = 'Debe especificar el diagnóstico.';
        return;
      }

      const name = this.isOtherDiagnosisSelected
        ? specification
        : selected.name;
      const duplicated = list.some((x) =>
        this.isOtherDiagnosisSelected
          ? (x.diagnosisId || x.id) === 256 &&
            this.normalizeText(x.name) === this.normalizeText(name)
          : (x.diagnosisId || x.id) === selected.id
      );

      if (duplicated) {
        this.diagnosisError = 'Este diagnóstico ya fue añadido a la lista.';
        return;
      }

      list.push({
        id: null,
        diagnosisId: selected.id,
        name,
        other: this.isOtherDiagnosisSelected ? name : null,
        sortOrder: list.length + 1,
        key: `${selected.id}-${Date.now()}-${list.length}`,
      });

      this.setDiagnoses(list);
      this.$set(this.model, 'diagnosis', null);
      this.$set(this.model, 'diagnosisSpecification', null);
      this.diagnosisError = null;
    },
    removeDiagnosis(index) {
      if (this.readOnly) return;

      const list = [...(this.model.diagnoses || [])];
      list.splice(index, 1);
      this.setDiagnoses(list);
    },
    onDiagnosisReordered() {
      this.setDiagnoses(this.model.diagnoses || []);
    },
    setDiagnoses(diagnoses) {
      this.$set(
        this.model,
        'diagnoses',
        diagnoses.map((diagnosis, index) => ({
          ...diagnosis,
          sortOrder: index + 1,
        }))
      );
    },
    normalizeText(value) {
      return (value || '').trim().toLowerCase();
    },
  },
};
</script>

<style lang="scss" scoped>
.disability-medical-info {
  background: #fdfdfb;
  padding: 8px 18px 24px;
}

.step-header {
  align-items: center;
  display: flex;
  gap: 12px;
  margin: 8px 0 18px;
}

.step-icon {
  align-items: center;
  background: #ff6d5f;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(23, 52, 47, 0.12);
  color: #fdfdfb;
  display: inline-flex;
  flex: 0 0 18px;
  font-size: 8px;
  height: 18px;
  justify-content: center;
  width: 18px;
}

.step-title {
  background: #e9e9e9;
  border-radius: 14px;
  color: #222f2c;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  min-width: 330px;
  padding: 7px 18px;
}

.section-panel {
  border: 1px solid #dfe4e1;
  border-radius: 6px;
  margin-bottom: 24px;
  overflow: visible;
  position: relative;
}

.category-panel {
  z-index: 2;
}

.section-panel__title {
  background: #f4f5f4;
  border-bottom: 1px solid #dfe4e1;
  color: #17342f;
  font-size: 15px;
  font-weight: 700;
  padding: 15px 18px;
}

.section-panel__body {
  background: #fafbfa;
  padding: 22px 22px 24px;
}

.category-field {
  max-width: 360px;
}

.diagnosis-selector {
  align-items: flex-end;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(240px, 1fr) minmax(220px, 1fr) 104px;
  margin-top: 26px;
  max-width: 960px;
}

.diagnosis-selector--single-field {
  grid-template-columns: minmax(240px, 1fr) 104px;
  max-width: 580px;
}

.diagnosis-selector__button {
  height: 36px;
  margin-bottom: 0;
  width: 104px;
}

.diagnosis-list {
  margin-top: 22px;
}

.diagnosis-list__separator {
  align-items: center;
  color: #fdfdfb;
  display: grid;
  font-size: 13px;
  font-weight: 700;
  grid-template-columns: 1fr auto 1fr;
  gap: 18px;
  margin-bottom: 18px;
}

.diagnosis-list__separator::before,
.diagnosis-list__separator::after {
  background: #b9c7c3;
  content: '';
  height: 1px;
}

.diagnosis-list__separator span {
  background: #294348;
  border-radius: 18px;
  padding: 7px 16px;
}

.diagnosis-row {
  align-items: center;
  background: #fff;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  min-height: 44px;
  padding: 8px 14px;
}

.diagnosis-row__leading,
.diagnosis-row__actions {
  align-items: center;
  display: flex;
}

.diagnosis-row__leading {
  gap: 12px;
  min-width: 0;
}

.diagnosis-row__index {
  color: #9eb0ac;
  font-weight: 700;
  min-width: 18px;
}

.diagnosis-row__drag,
.diagnosis-row__remove {
  background: transparent;
  border: 0;
  line-height: 1;
  padding: 0;
}

.diagnosis-row__drag {
  color: #c5cfcc;
  cursor: grab;
}

.diagnosis-row__drag:disabled {
  cursor: default;
}

.diagnosis-row__name {
  color: #17342f;
  font-weight: 700;
}

.diagnosis-row__actions {
  gap: 14px;
  margin-left: 18px;
}

.diagnosis-row__badge {
  border-radius: 18px;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 14px;
}

.diagnosis-row__badge--primary {
  background: #c7f8eb;
  color: #2db782;
}

.diagnosis-row__badge--secondary {
  background: #ffe4e6;
  color: #ff5b5c;
}

.diagnosis-row__remove {
  color: #ff5b5c;
}

.boolean-question {
  align-items: center;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 42px;
  padding: 6px 0;
}

.boolean-question__label {
  color: #17342f;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
}

.question-spacing {
  margin-top: 20px;
}

.question-last {
  border-top: 1px solid #e5e9e7;
  margin-top: 20px;
  padding-top: 20px;
}

.ambulatory-question {
  min-height: 48px;
  padding: 8px 0;
}

.ambulatory-question--divider {
  border-bottom: 1px solid #e5e9e7;
  margin-bottom: 18px;
  padding-bottom: 18px;
}

.ambulatory-question--short {
  gap: 28px;
  grid-template-columns: auto auto;
  justify-content: flex-start;
  max-width: 560px;
}

.ambulatory-question--last {
  border-top: 1px solid #e5e9e7;
  margin-top: 20px;
  padding-top: 20px;
}

.question-textarea {
  margin-top: 12px;
}

.short-field {
  max-width: 520px;
  padding: 0 0 20px;
}

.short-field--compact {
  padding-top: 4px;
}

.ambulatory-panel {
  color: #111;
}

.ambulatory-panel .section-panel__title,
.ambulatory-panel .boolean-question__label {
  color: #111;
}

.ambulatory-panel ::v-deep .custom-input-label,
.ambulatory-panel ::v-deep .data-label,
.ambulatory-panel ::v-deep .custom-control-label {
  color: #111 !important;
}

::v-deep .boolean-question__radio {
  min-width: 110px;

  > .col-12 {
    align-items: center;
    display: flex;
    gap: 18px;
    justify-content: flex-end;
    padding: 0;
  }

  .custom-control {
    margin: 0;
    min-height: 18px;
    padding-left: 1.35rem;
  }

  .custom-control-label {
    color: #17342f;
    font-size: 14px;
    line-height: 1.25;
  }

  .custom-control-label::before,
  .custom-control-label::after {
    height: 16px;
    left: -1.35rem;
    top: 0.05rem;
    width: 16px;
  }

  .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #2db782;
    border-color: #2db782;
  }

  .custom-control-input.custom-control-input:disabled:checked
    ~ .custom-control-label::before {
    background-color: #3f5b60 !important;
    border-color: #3f5b60 !important;
  }
}

::v-deep .question-textarea textarea {
  min-height: 86px;
}

::v-deep .question-textarea .custom-input-label,
::v-deep .short-field .custom-input-label {
  color: #17342f;
  font-size: 13px;
  font-weight: 500;
}

@media (max-width: 575.98px) {
  .disability-medical-info {
    padding: 8px 0 16px;
  }

  .step-title {
    font-size: 14px;
    min-width: 0;
    width: 100%;
  }

  .boolean-question {
    align-items: flex-start;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  ::v-deep .boolean-question__radio > .col-12 {
    justify-content: flex-start;
  }
}
</style>
